import type {
  AvailableLocale,
  LocaleMessages,
} from '../LanguageProvider/shared';
import './polyfills';

export const DEFAULT_LOCALE: AvailableLocale = 'en-US';

/**
 * Compiled file per locale. The files are named by region code, which is not
 * the key the app uses for the locale (`de` → `de-DE.json`), so the mapping
 * cannot be derived from the locale alone.
 */
const TRANSLATION_FILE: Record<AvailableLocale, string> = {
  af: 'af-ZA',
  am: 'am-ET',
  ar: 'ar',
  az: 'az-AZ',
  be: 'be-BY',
  bg: 'bg-BG',
  bn: 'bn-IN',
  bs: 'bs-BA',
  ca: 'ca',
  cs: 'cs-CZ',
  da: 'da-DK',
  de: 'de-DE',
  el: 'el-GR',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  es: 'es-ES',
  'es-MX': 'es-MX',
  et: 'et-EE',
  'fa-AF': 'fa-AF',
  'fa-IR': 'fa-IR',
  fi: 'fi-FI',
  fr: 'fr-FR',
  'fr-CA': 'fr-CA',
  gu: 'gu-IN',
  ha: 'ha',
  he: 'he-IL',
  'hi-IN': 'hi-IN',
  hr: 'hr-HR',
  ht: 'ht-HT',
  hu: 'hu-HU',
  hy: 'hy-AM',
  id: 'id-ID',
  is: 'is-IS',
  it: 'it-IT',
  ja: 'ja-JP',
  ka: 'ka-GE',
  kk: 'kk-KZ',
  kn: 'kn-IN',
  ko: 'ko-KR',
  lb: 'lb',
  lt: 'lt-LT',
  lv: 'lv-LV',
  mk: 'mk-MK',
  ml: 'ml-IN',
  mn: 'mn-MN',
  mr: 'mr-IN',
  ms: 'ms-MY',
  nl: 'nl-NL',
  no: 'no-NO',
  pa: 'pa-IN',
  pl: 'pl-PL',
  ps: 'ps',
  pt: 'pt-BR',
  'pt-PT': 'pt-PT',
  ro: 'ro-RO',
  ru: 'ru-RU',
  si: 'si-LK',
  sk: 'sk-SK',
  sl: 'sl-SI',
  so: 'so',
  sq: 'sq-AL',
  sr: 'sr-RS',
  sv: 'sv-SE',
  sw: 'sw',
  ta: 'ta-IN',
  te: 'te-IN',
  th: 'th-TH',
  tl: 'tl-PH',
  tr: 'tr-TR',
  uk: 'uk-UA',
  ur: 'ur',
  uz: 'uz-UZ',
  vi: 'vi-VN',
  'zh-CN': 'zh-CN',
  'zh-HK': 'zh-HK',
  'zh-TW': 'zh-TW',
};

async function importCompiled(file: string): Promise<LocaleMessages> {
  const compiled = await import(`../translations/compiled/${file}.json`);
  return (compiled.default ?? compiled) as LocaleMessages;
}

/**
 * One locale's compiled messages. The dynamic specifier is load-bearing: the
 * bundler emits a chunk per locale, so a page carries only the language it
 * renders. A static map would put every locale in the bundle of every layout that
 * imports it.
 *
 * Callers resolve this on the server and hand the result to
 * {@link LanguageProvider}, so the first render already has its copy. The
 * authenticated layout awaits it, so a locale whose file fails to load gets
 * the en-US set rather than an error page.
 */
export async function loadTranslationMessages(
  locale: AvailableLocale
): Promise<LocaleMessages> {
  return (await loadLocaleMessages(locale)).messages;
}

/**
 * {@link loadTranslationMessages}, with the locale whose messages loaded: en-US
 * when the requested file failed. A caller that sets the page's language,
 * direction or formats from the result reads this one, so English copy never
 * renders right to left or under another language's tag.
 */
export async function loadLocaleMessages(
  locale: AvailableLocale
): Promise<{ locale: AvailableLocale; messages: LocaleMessages }> {
  const fallbackFile = TRANSLATION_FILE[DEFAULT_LOCALE];
  const file = TRANSLATION_FILE[locale] ?? fallbackFile;
  try {
    return { locale, messages: await importCompiled(file) };
  } catch (error) {
    if (file === fallbackFile) {
      throw error;
    }
    console.error(
      `[i18n] Failed to load messages for ${locale}; using ${DEFAULT_LOCALE}`,
      error
    );
    return {
      locale: DEFAULT_LOCALE,
      messages: await importCompiled(fallbackFile),
    };
  }
}
